import { useState, useCallback, useEffect, useRef } from 'react';
import {
  Table,
  TableHeader,
  TableBody,
  TableColumn,
  TableRow,
  TableCell,
  Button,
  Chip,
  Modal,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  useDisclosure,
  Spinner,
  Input,
  Select,
  SelectItem,
  Autocomplete,
  AutocompleteItem,
  Checkbox,
  Form,
  Textarea,
  Tooltip,
  Popover,
  PopoverTrigger,
  PopoverContent,
  Dropdown,
  DropdownTrigger,
  DropdownMenu,
  DropdownItem,
} from '@/components/ui';
import { Icon } from '@iconify/react/dist/iconify.js';
import { NameAvatar } from '@/components/common/output/name';
import { useCampaigns } from '@/hooks/communications/useCampaigns';
import { useApiWithAuth } from '@/hooks/api';
import type {
  CampaignResponse,
  CampaignState,
  CreateCampaignRequest,
  UpdateCampaignRequest,
  SnapshotMode,
} from '@/shared/communications/types';
import type {
  PcoList,
  PcoListResultResponse,
} from '@/shared/communications/types';
import {
  estimateSmsCost,
  getSmsSegmentCount,
} from '@/shared/communications/utils';
import { timeAgo } from '@/lib/datetime';

const CAMPAIGN_COLUMNS: { key: string }[] = [
  { key: 'name' },
  { key: 'pcoList' },
  { key: 'status' },
  { key: 'recipients' },
  { key: 'delivered' },
  { key: 'cost' },
  { key: 'created' },
  { key: 'updated' },
  { key: 'actions' },
];

const COLUMN_LABELS: Record<string, string> = {
  name: 'NAME',
  pcoList: 'PCO LIST',
  status: 'STATUS',
  recipients: 'RECIPIENTS',
  delivered: 'DELIVERED',
  cost: 'ESTIMATED COST',
  created: 'CREATED',
  updated: 'UPDATED',
  actions: 'ACTIONS',
};

const stateLabels: Record<CampaignState, string> = {
  draft: 'Draft',
  outbox: 'Sending',
  sent: 'Sent',
};

const CAMPAIGN_CHIP_CLASS =
  'rounded-md border border-slate-200 bg-slate-100 px-3 py-1.5 text-xs text-slate-700';

const USD_FORMATTER = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
});

/** Cost per SMS segment in USD (used for estimated cost) */
const COST_PER_SEGMENT = 0.0062;

export function Communications() {
  const { campaigns, loading, refetch } = useCampaigns();
  const { invoke } = useApiWithAuth();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const createModal = useDisclosure();
  const editModal = useDisclosure();
  const [selectedCampaign, setSelectedCampaign] =
    useState<CampaignResponse | null>(null);
  const [sending, setSending] = useState(false);
  const [sendResult, setSendResult] = useState<{
    success: boolean;
    message: string;
  } | null>(null);
  const [archivingId, setArchivingId] = useState<string | null>(null);

  // Create campaign form
  const [pcoLists, setPcoLists] = useState<PcoList[]>([]);
  const [listsLoading, setListsLoading] = useState(false);
  const [createName, setCreateName] = useState('');
  const [createPcoListId, setCreatePcoListId] = useState('');
  const [createPcoListQuery, setCreatePcoListQuery] = useState('');
  const [createSnapshotMode, setCreateSnapshotMode] =
    useState<SnapshotMode>('frozen');
  const [createUniqueRecipients, setCreateUniqueRecipients] = useState(true);
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);

  // Edit campaign form (draft only)
  const [editingCampaign, setEditingCampaign] =
    useState<CampaignResponse | null>(null);
  const [editName, setEditName] = useState('');
  const [editMessageBody, setEditMessageBody] = useState('');
  const [editPcoListId, setEditPcoListId] = useState('');
  const [editPcoListQuery, setEditPcoListQuery] = useState('');
  const [editSnapshotMode, setEditSnapshotMode] =
    useState<SnapshotMode>('frozen');
  const [editUniqueRecipients, setEditUniqueRecipients] = useState(true);
  const [editSaving, setEditSaving] = useState(false);
  const [editError, setEditError] = useState<string | null>(null);

  // List result preview (edit modal) — fetch on button click only
  const [listResultRequested, setListResultRequested] = useState(false);
  const [listResultRows, setListResultRows] = useState<unknown[]>([]);
  const [listResultNextOffset, setListResultNextOffset] = useState<
    number | null
  >(null);
  const [listResultNextCursor, setListResultNextCursor] = useState<
    string | null
  >(null);
  const [listResultLoading, setListResultLoading] = useState(false);
  const [listResultError, setListResultError] = useState<string | null>(null);
  const [editListTotalPeople, setEditListTotalPeople] = useState<number | null>(
    null,
  );

  const loadPcoLists = useCallback(
    async (nameQuery: string) => {
      try {
        setListsLoading(true);
        const params = new URLSearchParams();
        if (nameQuery.trim()) {
          params.set('name', nameQuery.trim());
        }
        const qs = params.toString();
        const url = qs
          ? `/communications/pco/lists?${qs}`
          : '/communications/pco/lists';
        const res = (await invoke(url, { method: 'GET' })) as {
          data: PcoList[];
        };
        setPcoLists(res.data || []);
      } catch (e) {
        console.error('Error loading PCO lists:', e);
        setPcoLists([]);
      } finally {
        setListsLoading(false);
      }
    },
    [invoke],
  );

  const invokeRef = useRef(invoke);
  invokeRef.current = invoke;

  const loadPcoListsRef = useRef(loadPcoLists);
  loadPcoListsRef.current = loadPcoLists;

  const createListsLastQueryRef = useRef<string | null>(null);
  const editListsLastQueryRef = useRef<string | null>(null);

  // Debounced fetch of PCO lists when user types in create modal autocomplete
  useEffect(() => {
    if (!createModal.isOpen) {
      return undefined;
    }
    const query = createPcoListQuery;
    if (query === createListsLastQueryRef.current) {
      return undefined;
    }
    const t = setTimeout(() => {
      createListsLastQueryRef.current = query;
      loadPcoListsRef.current(query);
    }, 400);
    return () => clearTimeout(t);
  }, [createModal.isOpen, createPcoListQuery]);

  // Debounced fetch of PCO lists when user types in edit modal autocomplete
  useEffect(() => {
    if (!editModal.isOpen) {
      return undefined;
    }
    const query = editPcoListQuery;
    if (query === editListsLastQueryRef.current) {
      return undefined;
    }
    const t = setTimeout(() => {
      editListsLastQueryRef.current = query;
      loadPcoListsRef.current(query);
    }, 400);
    return () => clearTimeout(t);
  }, [editModal.isOpen, editPcoListQuery]);

  useEffect(() => {
    if (!createModal.isOpen) {
      createListsLastQueryRef.current = null;
    }
  }, [createModal.isOpen]);

  useEffect(() => {
    if (!editModal.isOpen) {
      editListsLastQueryRef.current = null;
    }
  }, [editModal.isOpen]);

  useEffect(() => {
    if (editModal.isOpen && editPcoListId) {
      let cancelled = false;
      (async () => {
        try {
          const res = (await invokeRef.current(
            `/communications/pco/lists/${encodeURIComponent(editPcoListId)}`,
            { method: 'GET' },
          )) as { total_people?: number | null };
          if (!cancelled) {
            setEditListTotalPeople(
              typeof res.total_people === 'number' ? res.total_people : null,
            );
          }
        } catch {
          if (!cancelled) {
            setEditListTotalPeople(null);
          }
        }
      })();
      return () => {
        cancelled = true;
      };
    }
    if (!editModal.isOpen) {
      setEditListTotalPeople(null);
    }
    return undefined;
  }, [editModal.isOpen, editPcoListId]);

  // When the selected PCO list changes in the edit modal, reset list result preview
  useEffect(() => {
    if (editModal.isOpen) {
      setListResultRequested(false);
      setListResultRows([]);
      setListResultNextOffset(null);
      setListResultNextCursor(null);
      setListResultError(null);
    }
  }, [editModal.isOpen, editPcoListId]);

  const openCreateModal = useCallback(() => {
    setCreateName('');
    setCreatePcoListId('');
    setCreatePcoListQuery('');
    setCreateSnapshotMode('frozen');
    setCreateUniqueRecipients(true);
    setCreateError(null);
    createModal.onOpen();
    createListsLastQueryRef.current = '';
    loadPcoLists('');
  }, [createModal, loadPcoLists]);

  const handleCreateSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!createName.trim() || !createPcoListId) {
      setCreateError('Name and PCO list are required.');
      return;
    }
    setCreating(true);
    setCreateError(null);
    try {
      const payload: CreateCampaignRequest = {
        name: createName.trim(),
        pcoListId: createPcoListId,
        snapshotMode: createSnapshotMode,
        options: { uniqueRecipientsByPhone: createUniqueRecipients },
      };
      await invoke('/communications/campaigns', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      await refetch();
      createModal.onClose();
    } catch (err) {
      setCreateError(
        err instanceof Error ? err.message : 'Failed to create campaign.',
      );
    } finally {
      setCreating(false);
    }
  };

  const openEditModal = useCallback(
    (campaign: CampaignResponse) => {
      setEditingCampaign(campaign);
      const c = campaign.data;
      setEditName(c.name);
      setEditMessageBody(c.messageBody ?? '');
      setEditPcoListId(c.pcoListId);
      setEditPcoListQuery(c.pcoListName ?? '');
      setEditSnapshotMode(c.snapshotMode);
      setEditUniqueRecipients(c.options?.uniqueRecipientsByPhone ?? true);
      setEditError(null);
      setListResultRequested(false);
      setListResultRows([]);
      setListResultNextOffset(null);
      setListResultNextCursor(null);
      setListResultLoading(false);
      setListResultError(null);
      editModal.onOpen();
      editListsLastQueryRef.current = editPcoListQuery;
      loadPcoLists(c.pcoListName ?? '');
      setEditListTotalPeople(null);
    },
    // editPcoListQuery is set from campaign inside the callback, not a reactive dep
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [editModal, loadPcoLists],
  );

  const handleEditSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingCampaign) {
      return;
    }
    setEditSaving(true);
    setEditError(null);
    try {
      const payload: UpdateCampaignRequest = {
        name: editName.trim(),
        messageBody: editMessageBody,
        pcoListId: editPcoListId || undefined,
        pcoListName: pcoLists.find((l) => l.id === editPcoListId)?.name,
        snapshotMode: editSnapshotMode,
        options: { uniqueRecipientsByPhone: editUniqueRecipients },
      };
      await invoke(`/communications/campaigns/${editingCampaign.id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      await refetch();
      editModal.onClose();
    } catch (err) {
      setEditError(
        err instanceof Error ? err.message : 'Failed to update campaign.',
      );
    } finally {
      setEditSaving(false);
    }
  };

  const fetchListResult = useCallback(
    async (offset: number, cursor: string | null, append: boolean) => {
      if (!editingCampaign?.data.pcoListId) {
        return;
      }
      if (!append) {
        setListResultRequested(true);
      }
      setListResultError(null);
      setListResultLoading(true);
      const listId = editPcoListId ?? editingCampaign?.data.pcoListId;
      if (!listId) {
        setListResultError('Select a PCO list to preview.');
        setListResultLoading(false);
        return;
      }
      try {
        const url =
          cursor !== null && cursor !== undefined && cursor !== ''
            ? `/communications/pco/lists/${listId}/list-result?cursor=${encodeURIComponent(cursor)}`
            : `/communications/pco/lists/${listId}/list-result?offset=${offset}`;
        const res = (await invoke(url, { method: 'GET' })) as
          | PcoListResultResponse
          | { error?: string };

        if (res && 'error' in res && typeof res.error === 'string') {
          setListResultError(res.error);
          if (!append) {
            setListResultRows([]);
          }
          setListResultNextOffset(null);
          setListResultNextCursor(null);
          return;
        }

        const data = Array.isArray((res as PcoListResultResponse)?.data)
          ? (res as PcoListResultResponse).data
          : [];
        const next = res as PcoListResultResponse;
        setListResultRows((prev) => (append ? [...prev, ...data] : data));
        setListResultNextOffset(next.nextOffset ?? null);
        setListResultNextCursor(next.nextCursor ?? null);
      } catch (err) {
        setListResultError(
          err instanceof Error ? err.message : 'Failed to load list results',
        );
        if (!append) {
          setListResultRows([]);
        }
        setListResultNextOffset(null);
        setListResultNextCursor(null);
      } finally {
        setListResultLoading(false);
      }
    },
    [editPcoListId, editingCampaign?.data.pcoListId, invoke],
  );

  const handlePreviewListResults = useCallback(() => {
    fetchListResult(0, null, false);
  }, [fetchListResult]);

  const handleLoadMoreListResults = useCallback(() => {
    if (
      listResultNextCursor !== null &&
      listResultNextCursor !== undefined &&
      listResultNextCursor !== ''
    ) {
      fetchListResult(0, listResultNextCursor, true);
    } else if (listResultNextOffset !== null) {
      fetchListResult(listResultNextOffset, null, true);
    }
  }, [fetchListResult, listResultNextCursor, listResultNextOffset]);

  const handleSendClick = (campaign: CampaignResponse) => {
    setSelectedCampaign(campaign);
    setSendResult(null);
    onOpen();
  };

  const handleConfirmSend = async () => {
    if (!selectedCampaign) {
      return;
    }

    setSending(true);
    setSendResult(null);

    try {
      await invoke(`/communications/campaigns/${selectedCampaign.id}/send`, {
        method: 'POST',
      });
      setSendResult({
        success: true,
        message: `Campaign queued for sending to ${selectedCampaign.data.sendableRecipientCount} recipients.`,
      });
    } catch (error) {
      setSendResult({
        success: false,
        message:
          error instanceof Error ? error.message : 'Failed to send campaign.',
      });
    } finally {
      setSending(false);
    }
  };

  const handleArchive = async (campaign: CampaignResponse) => {
    setArchivingId(campaign.id);
    try {
      await invoke(`/communications/campaigns/${campaign.id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ archived: true }),
      });
      await refetch();
    } catch (err) {
      console.error('Failed to archive campaign:', err);
    } finally {
      setArchivingId(null);
    }
  };

  if (loading) {
    return (
      <div className='flex flex-col items-center justify-center gap-3 p-12'>
        <Spinner size='lg' />
        <p className='text-sm text-slate-500'>Loading campaigns...</p>
      </div>
    );
  }

  return (
    <div className='p-4'>
      <div className='mb-4 flex items-center justify-between'>
        <h2 className='text-xl font-semibold'>SMS Campaigns</h2>
        <Button
          color='primary'
          onPress={openCreateModal}
          startContent={<Icon icon='mdi:plus' width={18} />}
        >
          New campaign
        </Button>
      </div>

      {campaigns.length === 0 ? (
        <div className='rounded-xl border border-slate-200 bg-white p-8 text-center text-slate-500'>
          <p className='text-lg'>No campaigns found.</p>
          <p className='mt-2 text-sm'>
            Create a campaign to get started with SMS messaging.
          </p>
          <Button
            color='primary'
            className='mt-4'
            onPress={openCreateModal}
            startContent={<Icon icon='mdi:plus' width={18} />}
          >
            Create campaign
          </Button>
        </div>
      ) : (
        <Table>
          <TableHeader columns={CAMPAIGN_COLUMNS}>
            {(col: { key: string }) => (
              <TableColumn
                key={col.key}
                className={col.key === 'recipients' ? 'w-32' : undefined}
              >
                {COLUMN_LABELS[col.key]}
              </TableColumn>
            )}
          </TableHeader>
          <TableBody items={campaigns}>
            {(campaign) => {
              const c = campaign.data;

              return (
                <TableRow key={campaign.id}>
                  {(columnKey) => {
                    if (columnKey === 'name') {
                      return (
                        <TableCell>
                          <p className='font-medium'>{c.name}</p>
                        </TableCell>
                      );
                    }

                    if (columnKey === 'pcoList') {
                      return <TableCell>{c.pcoListName}</TableCell>;
                    }

                    if (columnKey === 'status') {
                      return (
                        <TableCell>
                          <Chip className={CAMPAIGN_CHIP_CLASS}>
                            {stateLabels[c.state]}
                          </Chip>
                        </TableCell>
                      );
                    }

                    if (columnKey === 'recipients') {
                      return (
                        <TableCell className='w-32'>
                          <div className='flex items-center gap-1 text-sm'>
                            <span>{c.sendableRecipientCount}</span>
                            {c.suppressedCount > 0 && (
                              <Popover>
                                <PopoverTrigger>
                                  <button
                                    type='button'
                                    className='ml-1 inline-flex h-4 w-4 items-center justify-center rounded-full bg-slate-100 text-[10px] text-slate-500 hover:bg-slate-200'
                                    aria-label={`${c.suppressedCount} suppressed recipients`}
                                  >
                                    <Icon
                                      icon='mdi:information-outline'
                                      width={12}
                                    />
                                  </button>
                                </PopoverTrigger>
                                <PopoverContent className='z-50 min-w-[18rem] max-w-md text-xs text-slate-700'>
                                  <p>
                                    {c.suppressedCount === 1
                                      ? '1 recipient is suppressed and will not receive this message.'
                                      : `${c.suppressedCount} recipients are suppressed and will not receive this message.`}
                                  </p>
                                </PopoverContent>
                              </Popover>
                            )}
                          </div>
                        </TableCell>
                      );
                    }

                    if (columnKey === 'delivered') {
                      return (
                        <TableCell>
                          {c.state !== 'draft' ? (
                            <div className='text-sm'>
                              <span className='text-emerald-600'>
                                {c.deliveredCount}
                              </span>
                              {c.failedCount > 0 && (
                                <span className='ml-1 text-red-600'>
                                  / {c.failedCount} failed
                                </span>
                              )}
                            </div>
                          ) : (
                            <span className='text-slate-300'>-</span>
                          )}
                        </TableCell>
                      );
                    }

                    if (columnKey === 'cost') {
                      let costLabel = '-';
                      if (c.actualCost !== null && c.actualCost !== undefined) {
                        costLabel = `$${c.actualCost.toFixed(2)}`;
                      } else if (c.estimatedCost > 0) {
                        costLabel = `$${c.estimatedCost.toFixed(2)}`;
                      }

                      return <TableCell>{costLabel}</TableCell>;
                    }

                    if (columnKey === 'created') {
                      const fullCreated = new Date(c.createdAt).toLocaleString(
                        undefined,
                        { dateStyle: 'full', timeStyle: 'medium' },
                      );

                      return (
                        <TableCell>
                          <Tooltip content={fullCreated}>
                            <span className='cursor-default text-sm text-slate-600'>
                              {timeAgo(c.createdAt)}
                            </span>
                          </Tooltip>
                        </TableCell>
                      );
                    }

                    if (columnKey === 'updated') {
                      const fullUpdated = new Date(c.updatedAt).toLocaleString(
                        undefined,
                        { dateStyle: 'full', timeStyle: 'medium' },
                      );

                      return (
                        <TableCell>
                          <Tooltip content={fullUpdated}>
                            <span className='cursor-default text-sm text-slate-600'>
                              {timeAgo(c.updatedAt)}
                            </span>
                          </Tooltip>
                        </TableCell>
                      );
                    }

                    if (columnKey === 'actions') {
                      return (
                        <TableCell>
                          <div className='flex items-center gap-2'>
                            {c.state === 'draft' && (
                              <>
                                <Button
                                  size='sm'
                                  variant='light'
                                  onPress={() => openEditModal(campaign)}
                                  startContent={
                                    <Icon icon='mdi:pencil' width={16} />
                                  }
                                >
                                  Edit
                                </Button>
                                {c.messageBody &&
                                c.sendableRecipientCount > 0 ? (
                                  <Button
                                    size='sm'
                                    variant='bordered'
                                    color='primary'
                                    onPress={() => handleSendClick(campaign)}
                                  >
                                    Send
                                  </Button>
                                ) : (
                                  !c.messageBody && (
                                    <Popover wrapperClassName='inline-flex'>
                                      <PopoverTrigger>
                                        <button
                                          type='button'
                                          className='inline-flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-md border border-amber-200 bg-amber-50 text-amber-600 hover:bg-amber-100'
                                          aria-label='No message — use Edit to add one'
                                        >
                                          <Icon
                                            icon='mdi:exclamation'
                                            width={18}
                                          />
                                        </button>
                                      </PopoverTrigger>
                                      <PopoverContent
                                        portal
                                        className='min-w-[20rem] max-w-md p-4 text-base text-slate-700'
                                      >
                                        <p>No message — use Edit to add one</p>
                                      </PopoverContent>
                                    </Popover>
                                  )
                                )}
                              </>
                            )}
                            {c.state === 'outbox' && (
                              <Chip className={CAMPAIGN_CHIP_CLASS}>
                                In progress
                              </Chip>
                            )}
                            {c.state === 'sent' && (
                              <Chip className={CAMPAIGN_CHIP_CLASS}>
                                Complete
                              </Chip>
                            )}
                            <Dropdown>
                              <DropdownTrigger>
                                <Button
                                  isIconOnly
                                  variant='light'
                                  size='sm'
                                  aria-label='Actions'
                                  isDisabled={archivingId === campaign.id}
                                >
                                  {archivingId === campaign.id ? (
                                    <Spinner size='sm' />
                                  ) : (
                                    <Icon
                                      icon='mdi:dots-vertical'
                                      width={20}
                                      height={20}
                                      className='text-slate-600'
                                    />
                                  )}
                                </Button>
                              </DropdownTrigger>
                              <DropdownMenu>
                                <DropdownItem
                                  key='archive'
                                  onPress={() => handleArchive(campaign)}
                                  startContent={
                                    <Icon
                                      icon='mdi:archive-outline'
                                      width={18}
                                    />
                                  }
                                >
                                  Archive
                                </DropdownItem>
                              </DropdownMenu>
                            </Dropdown>
                          </div>
                        </TableCell>
                      );
                    }

                    return null;
                  }}
                </TableRow>
              );
            }}
          </TableBody>
        </Table>
      )}

      <Modal isOpen={isOpen} onClose={onClose}>
        <ModalContent size='md'>
          {selectedCampaign && (
            <>
              <ModalHeader>
                {sendResult?.success ? 'Campaign Queued' : 'Confirm Send'}
              </ModalHeader>
              <ModalBody>
                {sendResult ? (
                  <p
                    className={
                      sendResult.success ? 'text-emerald-700' : 'text-red-600'
                    }
                  >
                    {sendResult.message}
                  </p>
                ) : (
                  <>
                    <p>
                      Send <strong>{selectedCampaign.data.name}</strong> to{' '}
                      <strong>
                        {selectedCampaign.data.sendableRecipientCount}
                      </strong>{' '}
                      recipients?
                    </p>
                    <div className='mt-3 rounded-lg bg-slate-100 p-3 text-sm'>
                      <p className='mb-1 font-medium'>Message preview:</p>
                      <p className='whitespace-pre-wrap text-slate-600'>
                        {selectedCampaign.data.messageBody}
                      </p>
                    </div>
                    <div className='mt-2 text-sm text-slate-500'>
                      <p>
                        Estimated cost: ~$
                        {selectedCampaign.data.estimatedCost.toFixed(2)}
                      </p>
                      <p>
                        Segments per message:{' '}
                        {selectedCampaign.data.estimatedSegments || 1}
                      </p>
                    </div>
                  </>
                )}
              </ModalBody>
              <ModalFooter>
                {sendResult?.success ? (
                  <Button color='primary' onPress={onClose}>
                    Done
                  </Button>
                ) : (
                  <>
                    <Button
                      variant='light'
                      onPress={onClose}
                      isDisabled={sending}
                    >
                      Cancel
                    </Button>
                    <Button
                      color='primary'
                      onPress={handleConfirmSend}
                      isLoading={sending}
                    >
                      {sending ? 'Sending...' : 'Send Now'}
                    </Button>
                  </>
                )}
              </ModalFooter>
            </>
          )}
        </ModalContent>
      </Modal>

      <Modal isOpen={createModal.isOpen} onClose={createModal.onClose}>
        <ModalContent size='md'>
          <Form onSubmit={handleCreateSubmit}>
            <ModalHeader>New campaign</ModalHeader>
            <ModalBody className='space-y-4'>
              {createError && (
                <p className='rounded-md bg-red-50 px-3 py-2 text-sm text-red-700'>
                  {createError}
                </p>
              )}
              <Input
                label='Campaign name'
                value={createName}
                onValueChange={setCreateName}
                placeholder='e.g. Sunday reminder'
                isRequired
              />
              <Autocomplete
                label='PCO list'
                placeholder='Type to search lists...'
                items={pcoLists}
                inputValue={
                  createPcoListId
                    ? (pcoLists.find((l) => l.id === createPcoListId)?.name ??
                      createPcoListQuery)
                    : createPcoListQuery
                }
                onInputChange={(value) => {
                  setCreatePcoListQuery(value);
                  const selectedName = createPcoListId
                    ? pcoLists.find((l) => l.id === createPcoListId)?.name
                    : undefined;
                  if (createPcoListId && value !== selectedName) {
                    setCreatePcoListId('');
                  }
                }}
                selectedKey={createPcoListId || null}
                onSelectionChange={(key) => {
                  const id = key ?? '';
                  setCreatePcoListId(id);
                  const list = pcoLists.find((l) => l.id === id);
                  setCreatePcoListQuery(list?.name ?? '');
                }}
                isLoading={listsLoading}
                listboxProps={{ emptyContent: 'No lists match' }}
              >
                {(item: PcoList) => (
                  <AutocompleteItem key={item.id} textValue={item.name}>
                    {item.name}
                  </AutocompleteItem>
                )}
              </Autocomplete>
              <Select
                label='Snapshot mode'
                placeholder='Select mode'
                items={[
                  { id: 'frozen', label: 'Frozen' },
                  { id: 'dynamic', label: 'Dynamic' },
                ]}
                selectedKeys={[createSnapshotMode]}
                onSelectionChange={(sel) =>
                  setCreateSnapshotMode(
                    (sel.currentKey as SnapshotMode) ?? 'frozen',
                  )
                }
              >
                {(item: { id: SnapshotMode; label: string }) => (
                  <SelectItem key={item.id} textValue={item.label}>
                    {item.label}
                  </SelectItem>
                )}
              </Select>
              <Checkbox
                isSelected={createUniqueRecipients}
                onValueChange={setCreateUniqueRecipients}
              >
                Deduplicate by phone (one message per number)
              </Checkbox>
            </ModalBody>
            <ModalFooter>
              <Button
                type='button'
                variant='light'
                onPress={createModal.onClose}
              >
                Cancel
              </Button>
              <Button type='submit' color='primary' isLoading={creating}>
                {creating ? 'Creating...' : 'Create campaign'}
              </Button>
            </ModalFooter>
          </Form>
        </ModalContent>
      </Modal>

      <Modal isOpen={editModal.isOpen} onClose={editModal.onClose}>
        <ModalContent size='md'>
          {editingCampaign && (
            <Form onSubmit={handleEditSubmit}>
              <ModalHeader>Edit campaign</ModalHeader>
              <ModalBody className='space-y-4'>
                {editError && (
                  <p className='rounded-md bg-red-50 px-3 py-2 text-sm text-red-700'>
                    {editError}
                  </p>
                )}
                <Input
                  label='Campaign name'
                  value={editName}
                  onValueChange={setEditName}
                  placeholder='e.g. Sunday reminder'
                />
                <Autocomplete
                  label='PCO list'
                  placeholder='Type to search lists...'
                  items={pcoLists}
                  inputValue={
                    editPcoListId
                      ? (pcoLists.find((l) => l.id === editPcoListId)?.name ??
                        editPcoListQuery)
                      : editPcoListQuery
                  }
                  onInputChange={(value) => {
                    setEditPcoListQuery(value);
                    const selectedName = editPcoListId
                      ? pcoLists.find((l) => l.id === editPcoListId)?.name
                      : undefined;
                    if (editPcoListId && value !== selectedName) {
                      setEditPcoListId('');
                    }
                  }}
                  selectedKey={editPcoListId || null}
                  onSelectionChange={(key) => {
                    const id = key ?? '';
                    setEditPcoListId(id);
                    const list = pcoLists.find((l) => l.id === id);
                    setEditPcoListQuery(list?.name ?? '');
                  }}
                  isLoading={listsLoading}
                  listboxProps={{ emptyContent: 'No lists match' }}
                >
                  {(item: PcoList) => (
                    <AutocompleteItem key={item.id} textValue={item.name}>
                      {item.name}
                    </AutocompleteItem>
                  )}
                </Autocomplete>
                {editListTotalPeople !== null && (
                  <div className='-mt-3 flex justify-end'>
                    <Chip className='rounded-md border border-emerald-100 bg-emerald-50 px-2.5 py-1 text-xs text-emerald-700'>
                      {editListTotalPeople}{' '}
                      {editListTotalPeople === 1 ? 'result' : 'results'}
                    </Chip>
                  </div>
                )}
                <Textarea
                  label='SMS message'
                  value={editMessageBody}
                  onValueChange={setEditMessageBody}
                  placeholder='Enter the message to send...'
                  minRows={4}
                />
                <div className='flex items-center justify-between text-xs text-slate-500'>
                  {(() => {
                    const { segments, encoding } = getSmsSegmentCount(
                      editMessageBody ?? '',
                    );
                    const previewCount = listResultRequested
                      ? (listResultRows ?? []).length
                      : null;
                    const recipientCount =
                      previewCount ??
                      editingCampaign?.data.sendableRecipientCount ??
                      0;
                    const cost = estimateSmsCost(
                      segments * recipientCount,
                      COST_PER_SEGMENT,
                    );
                    const segmentLabel =
                      segments === 1 ? '1 segment' : `${segments} segments`;
                    const encodingLabel =
                      editMessageBody.length > 0 ? ` (${encoding})` : '';

                    return (
                      <>
                        <span>{`Est. ${USD_FORMATTER.format(cost)}`}</span>
                        <span className='text-right'>
                          {segmentLabel}
                          {encodingLabel}
                        </span>
                      </>
                    );
                  })()}
                </div>
                <Select
                  label='Snapshot mode'
                  placeholder='Select mode'
                  items={[
                    { id: 'frozen', label: 'Frozen' },
                    { id: 'dynamic', label: 'Dynamic' },
                  ]}
                  selectedKeys={[editSnapshotMode]}
                  onSelectionChange={(sel) =>
                    setEditSnapshotMode(
                      (sel.currentKey as SnapshotMode) ?? 'frozen',
                    )
                  }
                >
                  {(item: { id: SnapshotMode; label: string }) => (
                    <SelectItem key={item.id} textValue={item.label}>
                      {item.label}
                    </SelectItem>
                  )}
                </Select>
                <Checkbox
                  isSelected={editUniqueRecipients}
                  onValueChange={setEditUniqueRecipients}
                >
                  Deduplicate by phone (one message per number)
                </Checkbox>
                <div className='space-y-2'>
                  <div className='flex items-center justify-between'>
                    <p className='text-sm font-medium text-slate-700'>
                      List result preview
                    </p>
                  </div>
                  {!listResultRequested ? (
                    <div className='flex flex-wrap items-center gap-2'>
                      <Button
                        type='button'
                        variant='flat'
                        size='sm'
                        onPress={handlePreviewListResults}
                        isLoading={listResultLoading}
                        isDisabled={listResultLoading}
                      >
                        {listResultLoading
                          ? 'Loading...'
                          : 'Preview list results'}
                      </Button>
                      {editListTotalPeople !== null && (
                        <Chip className='rounded-md border border-slate-200 bg-slate-100 px-2.5 py-1 text-xs text-slate-700'>
                          {editListTotalPeople}{' '}
                          {editListTotalPeople === 1 ? 'person' : 'people'}
                        </Chip>
                      )}
                    </div>
                  ) : (
                    <div className='space-y-2'>
                      {listResultError && (
                        <p className='rounded-md bg-red-50 px-3 py-2 text-sm text-red-700'>
                          {listResultError}
                        </p>
                      )}
                      <ul className='max-h-48 overflow-y-auto rounded-md border border-slate-200 bg-slate-50 p-2 text-sm'>
                        {(listResultRows ?? []).length === 0 &&
                          !listResultLoading && (
                            <li className='text-slate-500'>No rows returned</li>
                          )}
                        {(listResultRows ?? []).map((row, idx) => {
                          const item = row as {
                            id?: string;
                            attributes?: Record<string, unknown>;
                            primaryPhone?: string | null;
                          };
                          const attrs = item?.attributes ?? {};
                          let name: string;
                          if (
                            typeof attrs.first_name === 'string' ||
                            typeof attrs.last_name === 'string'
                          ) {
                            name = [attrs.first_name, attrs.last_name]
                              .filter(Boolean)
                              .join(' ');
                          } else if (typeof attrs.name === 'string') {
                            name = attrs.name;
                          } else {
                            name = item?.id ?? `Row ${idx + 1}`;
                          }

                          const phone =
                            typeof item.primaryPhone === 'string' &&
                            item.primaryPhone.trim().length > 0
                              ? item.primaryPhone
                              : (() => {
                                  const pn = (
                                    attrs as { phone_number?: string }
                                  ).phone_number;
                                  return typeof pn === 'string' ? pn : null;
                                })();

                          return (
                            <li
                              key={item?.id ?? idx}
                              className='flex items-center gap-2 border-b border-slate-200 py-1 last:border-b-0'
                            >
                              <NameAvatar
                                value={name}
                                size='sm'
                                className='shrink-0'
                              />
                              <div className='flex flex-col'>
                                <span>{name}</span>
                                {phone && (
                                  <span className='text-xs text-slate-500'>
                                    {phone}
                                  </span>
                                )}
                              </div>
                            </li>
                          );
                        })}
                      </ul>
                      {listResultNextOffset !== null ||
                      (listResultNextCursor !== null &&
                        listResultNextCursor !== undefined &&
                        listResultNextCursor !== '') ? (
                        <Button
                          type='button'
                          variant='flat'
                          size='sm'
                          onPress={handleLoadMoreListResults}
                          isLoading={listResultLoading}
                          isDisabled={listResultLoading}
                        >
                          {listResultLoading ? 'Loading...' : 'Load more'}
                        </Button>
                      ) : null}
                    </div>
                  )}
                </div>
              </ModalBody>
              <ModalFooter>
                <Button
                  type='button'
                  variant='light'
                  onPress={editModal.onClose}
                >
                  Cancel
                </Button>
                <Button type='submit' color='primary' isLoading={editSaving}>
                  {editSaving ? 'Saving...' : 'Save changes'}
                </Button>
              </ModalFooter>
            </Form>
          )}
        </ModalContent>
      </Modal>
    </div>
  );
}
